// Key Vault Configuration Service for React App
// This service loads OIDC configuration from Azure Key Vault via the Express API backend
// The backend retrieves secrets from Key Vault using managed identity and service endpoints

import { appConfig } from '../config/appConfig';
import { runtimeConfig } from './runtimeConfig';

interface OidcKeyVaultConfig {
  authority?: string;
  clientId?: string;
  authProvider?: string;
  jwksUri?: string;
}

class KeyVaultConfigService {
  private static instance: KeyVaultConfigService;
  private config: OidcKeyVaultConfig = {};
  private isInitialized = false;
  private isInitializing = false;

  private constructor() {}

  static getInstance(): KeyVaultConfigService {
    if (!KeyVaultConfigService.instance) {
      KeyVaultConfigService.instance = new KeyVaultConfigService();
    }
    return KeyVaultConfigService.instance;
  }

  // Initialize configuration from Key Vault via Express API backend
  async initialize(): Promise<void> {
    if (this.isInitialized || this.isInitializing) return;
    
    this.isInitializing = true;

    try {
      // Check if we're in local development mode
      if (this.isLocalDevelopment()) {
        console.log('🟡 Local development mode: Using environment variables for OIDC config');
        this.loadFromEnvironment();
        this.isInitialized = true;
        this.isInitializing = false;
        return;
      }

      // Resolve backend via runtime discovery
      try {
        await runtimeConfig.initialize();
      } catch {}
      const backendBase = runtimeConfig.getBackendUrl() || appConfig.api.baseUrl;
      const expressApiUrl = `${backendBase}/api/config/oidc`;
      console.log(`🔗 Fetching OIDC config from: ${expressApiUrl}`);
      
      const response = await fetch(expressApiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const keyVaultConfig = await response.json();
        this.config = {
          authority: keyVaultConfig.authority,
          clientId: keyVaultConfig.clientId,
          authProvider: keyVaultConfig.authProvider,
          jwksUri: keyVaultConfig.jwksUri
        };

        console.log('🔐 OIDC configuration loaded from Key Vault via Express API');
        console.log(`   Authority: ${this.config.authority || 'not set'}`);
        console.log(`   Client ID: ${this.config.clientId ? '***' + this.config.clientId.slice(-4) : 'not set'}`);
        console.log(`   Auth Provider: ${this.config.authProvider || 'not set'}`);
      } else {
        console.warn('Failed to load OIDC config from Key Vault, falling back to environment variables');
        this.loadFromEnvironment();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize OIDC configuration from Key Vault:', error);
      console.log('Falling back to environment variables');
      this.loadFromEnvironment();
      this.isInitialized = true;
    } finally {
      this.isInitializing = false;
    }
  }

  // Check if we're running in local development mode
  private isLocalDevelopment(): boolean {
    return (
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.port === '3000'
    );
  }

  // Load configuration from environment variables (fallback for local dev)
  private loadFromEnvironment(): void {
    this.config = {
      authority: process.env.REACT_APP_OIDC_AUTHORITY,
      clientId: process.env.REACT_APP_OIDC_CLIENT_ID,
      authProvider: process.env.REACT_APP_AUTH_PROVIDER,
      jwksUri: process.env.REACT_APP_OIDC_JWKS_URI,
    };

    console.log('🔐 OIDC configuration loaded from environment variables');
    console.log(`   Authority: ${this.config.authority || 'not set'}`);
    console.log(`   Client ID: ${this.config.clientId ? '***' + this.config.clientId.slice(-4) : 'not set'}`);
    console.log(`   Auth Provider: ${this.config.authProvider || 'not set'}`);
  }

  // Get OIDC authority URL (async to ensure initialization)
  async getAuthority(): Promise<string | undefined> {
    await this.initialize();
    return this.config.authority;
  }

  // Get OIDC client ID (async to ensure initialization)
  async getClientId(): Promise<string | undefined> {
    await this.initialize();
    return this.config.clientId;
  }

  // Get auth provider (async to ensure initialization)
  async getAuthProvider(): Promise<string | undefined> {
    await this.initialize();
    return this.config.authProvider;
  }

  // Get OIDC JWKS URI (async to ensure initialization)
  async getJwksUri(): Promise<string | undefined> {
    await this.initialize();
    return this.config.jwksUri;
  }

  // Get all configuration (async to ensure initialization)
  async getConfig(): Promise<OidcKeyVaultConfig> {
    await this.initialize();
    return { ...this.config };
  }

  // Synchronous getters for backward compatibility (deprecated - use async versions)
  getAuthoritySync(): string | undefined {
    return this.config.authority;
  }

  getClientIdSync(): string | undefined {
    return this.config.clientId;
  }
}

export default KeyVaultConfigService;
